import { queryOptions } from '@tanstack/react-query';

import { ONE_MINUTE } from '@/constants/time';
import { paths } from '@/types/api';
import { ApiError, fetchClient } from '@/util/fetchClient';
import { Sentry } from '@/util/sentry';

/**
 * Announcement configuration type derived from the v2 API schema.
 *
 * Represents the structure of announcement data returned by the `/v2/announcements` endpoint.
 * Supports different business logic types (survey, feature, general, referral) and visual variants
 * (info, success, warning, error) for flexible announcement display.
 *
 * Includes user targeting functionality:
 * - userTypes: Array of user types that should see the announcement ("all", "lmp", "expert")
 * - userIds: Optional array of specific user IDs that should see the announcement
 * - Union logic: user sees announcement if they match userTypes OR userIds
 *
 * The ctaAction property supports custom button behaviors:
 * - "copy_referral_code": Copies referral code to clipboard
 * - "navigate": Uses ctaUrl for navigation (default behavior)
 * - When not specified: Falls back to existing ctaUrl navigation
 */
type AnnouncementsResponse =
  paths['/v2/announcements']['get']['responses'][200]['content']['application/json'];

export type AnnouncementConfig = NonNullable<AnnouncementsResponse['data']>;

/**
 * Fetches every announcement this user is eligible for, in server priority order.
 *
 * Targeting is decided server-side, so an empty array means the user is targeted
 * by nothing live right now — it is not distinguishable from, and does not need
 * to be distinguished from, a failed request. The banner is absent either way.
 *
 * @returns Promise resolving to the eligible announcements, empty when there are none
 */
/**
 * Whether a failed announcements fetch is worth an engineer's attention.
 *
 * This query runs on every authenticated screen and `fetchClient` throws on any
 * non-OK response, so reporting every failure would turn two ordinary conditions
 * into a steady stream of issues: an expired token mid-refresh, and a phone that
 * lost signal. Neither says anything about this code, and the banner degrades to
 * absent either way.
 *
 * The noise filter in `util/sentryNoiseFilter.ts` cannot help here — it matches
 * on message text against known patterns, and an API error's message is whatever
 * the backend put in the body.
 */
function isUnexpectedAnnouncementFailure(error: unknown): boolean {
  if (error instanceof ApiError) {
    return error.status !== 401 && error.status !== 403;
  }

  // A fetch that never reached the server rejects with a TypeError rather than
  // an ApiError, which is what an offline device produces.
  return !(error instanceof TypeError);
}

/**
 * Every announcement the response offers, newest contract first.
 *
 * `announcements` carries the full priority-ordered list and `data` repeats its
 * first element, a pairing the API keeps so a client that shows one banner did
 * not have to change when rotation arrived. Reading the list and falling back to
 * the single pick means this survives either side of that contract without a
 * version check.
 */
export function selectAnnouncementList(
  response: AnnouncementsResponse | undefined
): AnnouncementConfig[] {
  const list = response?.announcements;

  if (list && list.length > 0) {
    return list;
  }

  return response?.data ? [response.data] : [];
}

async function getAnnouncementsV2(): Promise<AnnouncementConfig[]> {
  try {
    const { data } = await fetchClient.GET('/v2/announcements');

    return selectAnnouncementList(data);
  } catch (error) {
    if (isUnexpectedAnnouncementFailure(error)) {
      Sentry.captureException(error, {
        tags: {
          component: 'getAnnouncementsV2',
          source: 'announcements-fetch',
        },
        extra: {
          errorMessage: error instanceof Error ? error.message : String(error),
          status: error instanceof ApiError ? error.status : undefined,
        },
      });
    }

    return [];
  }
}

/**
 * React Query options for the eligible announcements, refreshed on a ~60s interval.
 *
 * The refresh interval is why a dismissal cannot be keyed to object identity:
 * the list is replaced wholesale every minute, and the payload carries no id.
 * See `buildAnnouncementSignature` in `hooks/useAnnouncements.ts`.
 *
 * @returns Query options configured for announcements with proper caching and refresh intervals
 *
 * @example
 * ```tsx
 * const { data: announcements = [] } = useQuery(getAnnouncementsV2QueryOptions(userId));
 * ```
 */
export function getAnnouncementsV2QueryOptions(userId: string | undefined) {
  return queryOptions({
    queryKey: ['announcementsV2', userId],
    queryFn: getAnnouncementsV2,
    staleTime: ONE_MINUTE,
    gcTime: ONE_MINUTE,
    refetchInterval: ONE_MINUTE,
  });
}
